import {
  Badge,
  Body1,
  Caption1,
  Dropdown,
  Option,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import FileIcon from '@modules/items/components/FileIcon';
import { AFFECTED_OBJECT_DECISION, TAffectedObjectDecision, TChangeV2 } from '../../models';
import { useChangeV2Store } from '../../state/ChangeV2Store';
import { TaskDocumentLinks } from '../shared/TaskDocumentLinks';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  intro: { color: tokens.colorNeutralForeground3 },
  wrap: {
    overflowX: 'auto',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  table: { minWidth: '600px', tableLayout: 'fixed' },
  colDocument: { width: '50%' },
  colDecision: { width: '25%' },
  colFunction: { width: '25%' },
  cell: { padding: tokens.spacingHorizontalM, verticalAlign: 'middle' },
  document: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  category: { color: tokens.colorNeutralForeground3 },
  fallback: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  field: { width: '100%', minWidth: 0 },
});

const DECISION_OPTIONS: TAffectedObjectDecision[] = Object.values(AFFECTED_OBJECT_DECISION);
const decisionColor = (decision: TAffectedObjectDecision) => {
  switch (decision) {
    case AFFECTED_OBJECT_DECISION.AFFECTED:
      return 'danger' as const;
    case AFFECTED_OBJECT_DECISION.REVIEW_REQUIRED:
      return 'warning' as const;
    case AFFECTED_OBJECT_DECISION.NOT_AFFECTED:
      return 'success' as const;
    default:
      return 'informative' as const;
  }
};

type TProps = { change: TChangeV2; readOnly: boolean };

export const AffectedObjectsTable = ({ change, readOnly }: TProps) => {
  const classes = useStyles();
  const { setAffectedObjectDecision } = useChangeV2Store();
  const assessment = change.assessment;
  if (!assessment) return null;

  return (
    <div className={classes.root}>
      <Subtitle2>Affected documents</Subtitle2>
      <Caption1 className={classes.intro}>
        Open the linked documents and optionally update their impact status.
      </Caption1>
      <div className={classes.wrap}>
        <Table aria-label="Affected documents" size="medium" className={classes.table}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell className={classes.colDocument}>Document</TableHeaderCell>
              <TableHeaderCell className={classes.colDecision}>Impact status</TableHeaderCell>
              <TableHeaderCell className={classes.colFunction}>Responsible function</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assessment.snapshot.candidateAffectedObjects.map((candidate) => {
              const state = assessment.affectedObjects.find((object) => object.objectId === candidate.id);
              const decision = state?.decision ?? AFFECTED_OBJECT_DECISION.NOT_ASSESSED;
              const documents =
                candidate.documents ??
                assessment.snapshot.preliminaryActivities
                  .filter((activity) => activity.linkedObject === candidate.label)
                  .flatMap((activity) => activity.documents ?? []);
              return (
                <TableRow key={candidate.id}>
                  <TableCell className={classes.cell}>
                    <div className={classes.document}>
                      {documents.length ? (
                        <>
                          <TaskDocumentLinks documents={documents} />
                          <Caption1 className={classes.category}>{candidate.label}</Caption1>
                        </>
                      ) : (
                        <div className={classes.fallback}>
                          <FileIcon
                            type="DOCUMENT"
                            path={[]}
                            isBlock={false}
                            isFrozen={false}
                            isTemplate={false}
                            size="small"
                          />
                          <Body1>{candidate.label}</Body1>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={classes.cell}>
                    {readOnly ? (
                      <Badge appearance="tint" color={decisionColor(decision)}>
                        {decision}
                      </Badge>
                    ) : (
                      <Dropdown
                        className={classes.field}
                        aria-label={`Impact status for ${candidate.label}`}
                        selectedOptions={[decision]}
                        value={decision}
                        onOptionSelect={(_, data) =>
                          setAffectedObjectDecision(
                            change.id,
                            candidate.id,
                            data.optionValue as TAffectedObjectDecision
                          )
                        }
                      >
                        {DECISION_OPTIONS.map((option) => (
                          <Option key={option} value={option} text={option}>
                            {option}
                          </Option>
                        ))}
                      </Dropdown>
                    )}
                  </TableCell>
                  <TableCell className={classes.cell}>
                    <Caption1>{candidate.responsibleFunction ?? '—'}</Caption1>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
